import { DateTime } from 'luxon';
import { Store, useStore } from 'dg-web-shared/lib/Flux.tsx';
import * as ClearanceCreateState from '../state/ClearanceCreateState.ts';
import { useLanguage } from 'dg-web-shared/lib/Localization.tsx';
import { NumberSingleSelectionSlideIn } from '../../ui/slidein/SingleSelectionSlideIn.tsx';
import { Translation } from '../../common/i18n/Text.ts';
import * as ClearanceCreateTexts from '../i18n/ClearanceCreateTexts.ts';
import { NumberMultiSelectionSlideIn } from '../../ui/slidein/MultiSelectionSlideIn.tsx';
import * as PermitTypeState from '../../common/state/PermitTypeState.ts';
import { FromDatePickerSlideIn, ToDatePickerSlideIn } from './Shared.tsx';
import { isDefined, Maybe } from 'dg-web-shared/lib/MaybeV2.ts';
import { LicensePlateCountrySlideIn } from '../../common/components/LicensePlateCountrySlideIn.tsx';
import { AutoActive } from 'product-shared/product-template/ProductOptions.tsx';

export interface LpSlideinTexts {
    Heading: Translation;
    Caption: Translation;
}

export function ClearanceLpCountrySlideIn() {
    const { storeState, update } = useStore(store => ({
        layout: ClearanceCreateState.Layout.get(store),
        create: ClearanceCreateState.Create.get(store),
    }));
    const { language } = useLanguage();

    return (
        <LicensePlateCountrySlideIn
            language={language}
            open={storeState.layout.lpCountrySelectionOpen}
            onSelect={(v: string, formValid: boolean) =>
                update((store: Store): string => {
                    ClearanceCreateState.Create.stateWrite(store, {
                        country: v,
                        licensePlateValid: formValid,
                    });
                    ClearanceCreateState.Layout.stateWrite(store, {
                        lpCountrySelectionOpen: false,
                    });
                    return 'LicensePlateCountrySlideIn-SelectLicensePlateCountry';
                })
            }
            country={storeState.create.country}
            licensePlateType={storeState.create.type}
            licensePlateNr={storeState.create.licensePlateNumber}
            onClose={() =>
                update(store =>
                    ClearanceCreateState.Layout.stateWrite(store, {
                        lpCountrySelectionOpen: false,
                    }),
                )
            }
            outsideBody
        />
    );
}

export function ClearanceValidFromSlideIn() {
    const { storeState, update } = useStore(store => ({
        layout: ClearanceCreateState.Layout.get(store),
        create: ClearanceCreateState.Create.get(store),
    }));
    const { language } = useLanguage();

    function onDateSelected(selectedDate: DateTime): void {
        update((store: Store): string => {
            ClearanceCreateState.Create.stateWrite(store, {
                validFrom: selectedDate,
            });
            ClearanceCreateState.Layout.stateWrite(store, {
                validFromDatePickerOpen: false,
            });
            return 'ClearanceValidFromSlideIn-selectDate';
        });
    }

    return (
        <FromDatePickerSlideIn
            open={storeState.layout.validFromDatePickerOpen}
            language={language}
            onClose={() =>
                update(store =>
                    ClearanceCreateState.Layout.stateWrite(store, {
                        validFromDatePickerOpen: false,
                    }),
                )
            }
            validFrom={storeState.create.validFrom}
            validTo={storeState.create.validTo}
            onSelect={(selectedDate: DateTime) => onDateSelected(selectedDate)}
        />
    );
}

export function ClearanceValidToSlideIn() {
    const { storeState, update } = useStore(store => ({
        layout: ClearanceCreateState.Layout.get(store),
        create: ClearanceCreateState.Create.get(store),
    }));
    const { language } = useLanguage();

    function onDateSelected(selectedDate: DateTime): void {
        update((store: Store): string => {
            ClearanceCreateState.Create.stateWrite(store, {
                validTo: selectedDate,
            });
            ClearanceCreateState.Layout.stateWrite(store, {
                validToDatePickerOpen: false,
            });
            return 'ClearanceValidToSlideIn-selectDate';
        });
    }

    return (
        <ToDatePickerSlideIn
            open={storeState.layout.validToDatePickerOpen}
            language={language}
            onClose={() =>
                update(store =>
                    ClearanceCreateState.Layout.stateWrite(store, {
                        validToDatePickerOpen: false,
                    }),
                )
            }
            validTo={storeState.create.validTo}
            onSelect={(selectedDate: DateTime) => onDateSelected(selectedDate)}
            validFrom={storeState.create.validFrom}
        />
    );
}

export interface PermitTypeSelectionTexts {
    PermitTypes: Translation;
}

export function ClearancePermitTypeSelectionSlideIn() {
    const { storeState, update } = useStore(store => ({
        layout: ClearanceCreateState.Layout.get(store),
        create: ClearanceCreateState.Create.get(store),
        permitTypes: PermitTypeState.get(store),
    }));
    const { language } = useLanguage();

    function txt(): PermitTypeSelectionTexts {
        return ClearanceCreateTexts.permitTypeSelection[language];
    }

    return (
        <NumberMultiSelectionSlideIn
            open={storeState.layout.permitTypeSelectionOpen}
            heading={txt().PermitTypes()}
            selection={storeState.create.permitTypes}
            options={storeState.permitTypes.data
                .filter(
                    pt =>
                        (pt.needsWhitelist ||
                            pt.autoActive === AutoActive.STARTS_TRANSACTION) &&
                        pt.operatorState === 'ACTIVE' &&
                        !pt.isWhitelistCustomer,
                )
                .map(pt => {
                    return { id: pt.id, displayText: pt.description };
                })}
            onLeftActionClick={() =>
                update(store =>
                    ClearanceCreateState.Layout.stateWrite(store, {
                        permitTypeSelectionOpen: false,
                    }),
                )
            }
            setNewSelection={(v: number[]) =>
                update(store =>
                    ClearanceCreateState.Create.setSelectedPermitTypes(
                        store,
                        v,
                    ),
                )
            }
            outsideBody
        />
    );
}

export interface PriceMultiplierSelectionSlideInTexts {
    Heading: Translation;
}

export function PriceMultiplierSelectionSlideIn() {
    const { storeState, update } = useStore(store => ({
        layout: ClearanceCreateState.Layout.get(store),
        create: ClearanceCreateState.Create.get(store),
        permitTypes: PermitTypeState.get(store),
    }));
    const { language } = useLanguage();

    function txt(): PriceMultiplierSelectionSlideInTexts {
        return ClearanceCreateTexts.priceMultiplierSelection[language];
    }

    const permitType: Maybe<PermitTypeState.PermitType> =
        storeState.permitTypes.data.find(
            pt => pt.id === storeState.layout.priceMultiplierOfPermitTypeOpen,
        );

    if (
        permitType &&
        permitType.allowedPriceModifiers &&
        permitType.allowedPriceModifiers.length > 0
    ) {
        return (
            <NumberSingleSelectionSlideIn
                open={isDefined(
                    storeState.layout.priceMultiplierOfPermitTypeOpen,
                )}
                heading={txt().Heading()}
                selection={storeState.create.priceModifierIds[permitType.id]}
                options={permitType.allowedPriceModifiers.map(m => {
                    return { id: m.priceModifierId, displayText: m.label };
                })}
                onSelect={(v: number) =>
                    update((store: Store): string => {
                        ClearanceCreateState.Create.setPriceMultiplier(store, {
                            id: permitType.id,
                            factor: v,
                        });
                        ClearanceCreateState.Layout.stateWrite(store, {
                            priceMultiplierOfPermitTypeOpen: null,
                        });
                        return 'ClearanceLpCountrySlideIn-SelectPriceMultiplier';
                    })
                }
                onLeftActionClick={() =>
                    update(store =>
                        ClearanceCreateState.Layout.stateWrite(store, {
                            priceMultiplierOfPermitTypeOpen: null,
                        }),
                    )
                }
                outsideBody
            />
        );
    } else {
        return (
            <NumberSingleSelectionSlideIn
                open={false}
                options={[]}
                heading={''}
                onLeftActionClick={() => null}
                onSelect={() => null}
                selection={null}
            />
        );
    }
}
